import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TreePine, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Field, Input } from "@/components/Primitives";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) { setError("The two new passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Password updated");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md panel p-8" data-testid="change-password-card">
        <div className="flex items-center gap-2 mb-6">
          <TreePine className="h-6 w-6 text-emerald-800" />
          <div className="font-display text-lg font-bold text-stone-950">Forestry WMS</div>
        </div>
        <h1 className="font-display text-2xl font-bold text-stone-950 flex items-center gap-2"><KeyRound className="h-5 w-5 text-emerald-800" /> {forced ? "Set your password" : "Change password"}</h1>
        <p className="text-sm text-stone-500 mt-1 mb-6">
          {forced
            ? `Welcome${user?.name ? `, ${user.name}` : ""}. You are signed in with a temporary password — choose your own before continuing.`
            : "Choose a new password for your account."}
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <Field label={forced ? "Temporary password" : "Current password"} required>
            <Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} autoComplete="current-password" data-testid="cp-current" />
          </Field>
          <Field label="New password" required>
            <Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} autoComplete="new-password" data-testid="cp-new" />
          </Field>
          <Field label="Confirm new password" required>
            <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} autoComplete="new-password" data-testid="cp-confirm" />
          </Field>
          <div className="text-xs text-stone-500">At least 8 characters, with upper- and lower-case letters and a number.</div>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="cp-error">{error}</div>}
          <Btn type="submit" disabled={saving} className="w-full justify-center" testid="cp-submit">{saving ? "Saving…" : "Save password"}</Btn>
        </form>
        <div className="mt-5 flex items-center justify-between text-xs text-stone-500">
          <span>Forgot it? Ask an administrator to reset it.</span>
          {forced
            ? <button type="button" onClick={async () => { await logout(); navigate("/login"); }} className="hover:text-stone-800 underline" data-testid="cp-signout">Sign out</button>
            : <button type="button" onClick={() => navigate("/")} className="hover:text-stone-800 underline" data-testid="cp-back">Back to dashboard</button>}
        </div>
      </div>
    </div>
  );
}
